// src/components/Absence/AbsenceDialog.js
import React, { useState, useEffect } from "react";
import checkPermission from '../Utils/checkPermission';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import AbsenceFormDialog from "./AbsenceFormDialog";
import DeleteConfirmDialog from "../Utils/DeleteConfirmDialog";
import axiosApi from "../Utils/axiosApi";

const AbsenceDialog = ({
  open,
  onClose,
  absences = [],
  onSave,
  hostelId,
  hostelName,
  hostelerId,
  hostelerName,
}) => {
  const [absenceList, setAbsenceList] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false); // now we will use this

  useEffect(() => {
    setAbsenceList(absences.map((a) => normalizeAbsence(a)));
  }, [absences]);

  const normalizeAbsence = (absence) => {
    if (!absence) return absence;
    return {
      ...absence,
      leaveStartDate: absence.leaveStartDate
        ? absence.leaveStartDate.substring(0, 10)
        : "",
      leaveEndDate: absence.leaveEndDate
        ? absence.leaveEndDate.substring(0, 10)
        : "",
      leaveRequestStatus: absence.leaveRequestStatus || "PENDING",
    };
  };

  const handleAdd = () => {
    setEditingAbsence(null);
    setFormOpen(true);
  };

  const handleEdit = async (absence) => {
    try {
      const response = await axiosApi.post("/hostelerLeaveRequest/getHostelLeaveRequest", {
        hostelLeaveRequestIds: [absence.id],
      });

      if (response.status === 200 && response.data?.length > 0) {
        setEditingAbsence(normalizeAbsence(response.data[0]));
      } else {
        setEditingAbsence(normalizeAbsence(absence));
      }
    } catch (err) {
      console.error("Error fetching absence:", err);
      setEditingAbsence(normalizeAbsence(absence));
    }
    setFormOpen(true);
  };

  const handleFormSave = (absence) => {
    const normalized = normalizeAbsence({
      ...absence,
      hostelId,
      hostelName,
      hostelerId,
      hostelerName,
    });

    let updated;
    if (editingAbsence) {
      updated = absenceList.map((a) =>
        a.id === normalized.id ? { ...a, ...normalized } : a
      );
    } else {
      updated = [...absenceList, normalized];
    }

    setAbsenceList(updated);
    if (onSave) onSave(updated);
  };

  const askDelete = (absence) => {
    setDeleteTarget(absence);
    setDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget?.id) {
      setDeleteOpen(false);
      return;
    }

    try {
      setDeleting(true); // indicate deletion in progress

      await axiosApi.post("/hostelerLeaveRequest/deleteHostelLeaveRequest", {
        hostelLeaveRequestIds: [deleteTarget.id],
      });
      const updated = absenceList.filter((a) => a.id !== deleteTarget.id);
      setAbsenceList(updated);
      if (onSave) onSave(updated);
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Manage Absences</DialogTitle>

      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hosteler Name</TableCell>
              <TableCell>Hostel Name</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {absenceList.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No absences recorded yet.
                </TableCell>
              </TableRow>
            )}

            {absenceList.map((absence) => (
              <TableRow key={absence.id || absence.leaveStartDate}>
                <TableCell>{absence.hostelerName || "-"}</TableCell>
                <TableCell>{absence.hostelName || "-"}</TableCell>
                <TableCell>{absence.leaveStartDate || "-"}</TableCell>
                <TableCell>{absence.leaveEndDate || "-"}</TableCell>
                <TableCell>{absence.remarks || "-"}</TableCell>
                <TableCell>
                  <Chip
                    label={absence.leaveRequestStatus}
                    color={
                      absence.leaveRequestStatus === "APPROVED"
                        ? "success"
                        : "warning"
                    }
                    size="small"
                  />
                </TableCell>

                <TableCell align="center">
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "center",
                    }}
                  >
				  
                    <Button
                      disabled={
        deleting ||
        (
          absence.leaveRequestStatus === "APPROVED" &&
          !checkPermission("Hostel", "Hosteler List", "EDITAPPROVEDABSENT")
        ) ||
        !checkPermission("Hostel", "Hosteler List", "UPDATEABSENT")
      }
                      size="small"
                      variant="outlined"
                      onClick={() => handleEdit(absence)}
                    >
                      Edit
                    </Button>
                    <Button
                      disabled={
        deleting ||
        (
          absence.leaveRequestStatus === "APPROVED" &&
          !checkPermission("Hostel", "Hosteler List", "DELETEAPPROVEDABSENT")
        ) ||
        !checkPermission("Hostel", "Hosteler List", "DELETEABSENT")
      }
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => askDelete(absence)}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>  

      <DialogActions> 
        <Button 
          disabled={!checkPermission("Hostel", "Hosteler List", "CREATEABSENT")}
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Absence
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (onSave) onSave(absenceList);
            onClose();
          }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Absence Form */}
      <AbsenceFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleFormSave}
        absence={editingAbsence}
        hostelId={hostelId}
        hostelName={hostelName}
        hostelerId={hostelerId}
        hostelerName={hostelerName}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Absence"
        message={`Are you sure you want to delete this absence from ${
          deleteTarget?.leaveStartDate || "-"
        } to ${deleteTarget?.leaveEndDate || "-"}?`}
      />
    </Dialog>
  );
};

export default AbsenceDialog;
